import {EmptyString, NonEmptyString} from 'Core/models/EmptyString';
import * as O from 'fp-ts/lib/Option';
import nanoid from 'nanoid';
import {EmptyChangeAddressRequest, ValidChangeAddress} from 'Agent/Policies/models/ServiceRequest/AddressChangeRequest';
import {matchChangeRequest} from 'Agent/Policies/models/ServiceRequest/ChangeRequest';
import {
  EmptyDirectBillingScheduleChange,
  ValidChangeDirectBillingSchedule,
} from 'Agent/Policies/models/ServiceRequest/DirectBillingScheduleChangeRequest';
import {
  ChangeType,
  PolicyNumber,
  PremiumMode,
  ValidInitialPolicyServiceRequest,
} from 'Agent/Policies/models/ServiceRequest/InitialPolicyServiceRequest';
import {
  EmptyChangePayrollAllotment,
  ValidChangePayrollAllotment,
} from 'Agent/Policies/models/ServiceRequest/PayrollAllotmentChangeRequest';
import {
  EmptyPolicyServiceRequest,
  matchPolicyServiceRequest,
  PolicyServiceRequest,
} from 'Agent/Policies/models/ServiceRequest/PolicyServiceRequest';
import {
  InvalidServiceRequestWithChange,
  ValidServiceRequestWithChange,
} from 'Agent/Policies/models/ServiceRequest/PolicyServiceRequestWithChangeRequest';
import {createAction} from 'Shared/utilities/actionCreator';

export enum PolicyServiceRequestActionType {
  CHANGE_POLICY_NUMBER = 'policyServiceRequest/CHANGE_POLICY_NUMBER',
  CHANGE_INSURED_LAST_NAME = 'policyServiceRequest/CHANGE_INSURED_LAST_NAME',
  CHANGE_INSURED_FIRST_NAME = 'policyServiceRequest/CHANGE_INSURED_FIRST_NAME',
  CHANGE_INSURED_MIDDLE_NAME = 'policyServiceRequest/CHANGE_INSURED_MIDDLE_NAME',
  CHANGE_PREMIUM_MODE = 'policyServiceRequest/CHANGE_PREMIUM_MODE',
  CHANGE_ACCOUNT_NUMBER = 'policyServiceRequest/CHANGE_ACCOUNT_NUMBER',
  CHANGE_CONTROL_NUMBER = 'policyServiceRequest/CHANGE_CONTROL_NUMBER',
  CHANGE_PAYOR_NAME = 'policyServiceRequest/CHANGE_PAYOR_NAME',
  CHANGE_PLACE_POLICY_ON_DIRECT_BILL = 'policyServiceRequest/CHANGE_PLACE_POLICY_ON_DIRECT_BILL',
  CHANGE_ADDRESS_LAST_NAME = 'policyServiceRequest/CHANGE_ADDRESS_LAST_NAME',
  CHANGE_ADDRESS_FIRST_NAME = 'policyServiceRequest/CHANGE_ADDRESS_FIRST_NAME',
  CHANGE_ADDRESS_MIDDLE_NAME = 'policyServiceRequest/CHANGE_ADDRESS_MIDDLE_NAME',
  CHANGE_ADDRESS_STREET = 'policyServiceRequest/CHANGE_ADDRESS_STREET',
  CHANGE_ADDRESS_CITY = 'policyServiceRequest/CHANGE_ADDRESS_CITY',
  CHANGE_ADDRESS_STATE = 'policyServiceRequest/CHANGE_ADDRESS_STATE',
  CHANGE_ADDRESS_ZIP = 'policyServiceRequest/CHANGE_ADDRESS_ZIP',
  CHANGE_SERVICE_REQUEST_TYPE = 'policyServiceRequest/CHANGE_SERVICE_REQUEST_TYPE',
  CHANGE_ADDITIONAL_POLICIES = 'policyServiceRequest/CHANGE_ADDITIONAL_POLICIES',
  CLEAR = 'policyServiceRequest/CLEAR',
  SUBMIT_INITIAL_REQUEST = 'policyServiceRequest/SUBMIT_INITIAL_REQUEST',
  ADD_ADDITIONAL_POLICY_NUMBER = 'policyServiceRequest/ADD_ADDITIONAL_POLICY_NUMBER',
  REMOVE_ADDITIONAL_POLICY_NUMBER = 'policyServiceRequest/REMOVE_ADDITIONAL_POLICY_NUMBER',
  SAVE_SERVICE_REQUEST = 'policyServiceRequest/SAVE_SERVICE_REQUEST',
}

export const clear = () => createAction(PolicyServiceRequestActionType.CLEAR);
export const savePolicyServiceRequest = (encryptedIdentifier: string, serviceRequest: ValidServiceRequestWithChange) =>
  createAction(PolicyServiceRequestActionType.SAVE_SERVICE_REQUEST, {encryptedIdentifier, serviceRequest});
export const submitInitialRequest = (validInitialRequest: ValidInitialPolicyServiceRequest) =>
  createAction(PolicyServiceRequestActionType.SUBMIT_INITIAL_REQUEST, {validInitialRequest});
export const changeRequestType = (changeType: ChangeType) =>
  createAction(PolicyServiceRequestActionType.CHANGE_SERVICE_REQUEST_TYPE, {changeType});
export const changeAdditionalPolicies = (additionalPolicyId: string, policyNumber: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDITIONAL_POLICIES, {additionalPolicyId, policyNumber});
export const addAdditionalPolicyNumber = () =>
  createAction(PolicyServiceRequestActionType.ADD_ADDITIONAL_POLICY_NUMBER);
export const removeAdditionalPolicyNumber = (additionalPolicyId: string) =>
  createAction(PolicyServiceRequestActionType.REMOVE_ADDITIONAL_POLICY_NUMBER, {additionalPolicyId});
export const changePolicyNumber = (policyNumber: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_POLICY_NUMBER, {policyNumber});
export const changeInsuredLastName = (insuredLastName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_INSURED_LAST_NAME, {insuredLastName});
export const changeInsuredFirstName = (insuredFirstName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_INSURED_FIRST_NAME, {insuredFirstName});
export const changeInsuredMiddleName = (insuredMiddleName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_INSURED_MIDDLE_NAME, {insuredMiddleName});
export const changePremiumMode = (premiumMode: PremiumMode) =>
  createAction(PolicyServiceRequestActionType.CHANGE_PREMIUM_MODE, {premiumMode});
export const changeAccountNumber = (accountNumber: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ACCOUNT_NUMBER, {accountNumber});
export const changeControlNumber = (controlNumber: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_CONTROL_NUMBER, {controlNumber});
export const changePayorName = (payorName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_PAYOR_NAME, {payorName});
export const changePlacePolicyOnDirectBill = (placePolicyOnBill: PremiumMode) =>
  createAction(PolicyServiceRequestActionType.CHANGE_PLACE_POLICY_ON_DIRECT_BILL, {placePolicyOnBill});
export const changeAddressLastName = (addressLastName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_LAST_NAME, {addressLastName});
export const changeAddressFirstName = (addressFirstName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_FIRST_NAME, {addressFirstName});
export const changeAddressMiddleName = (addressMiddleName: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_MIDDLE_NAME, {addressMiddleName});
export const changeAddressStreet = (addressStreet: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_STREET, {addressStreet});
export const changeAddressState = (addressState: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_STATE, {addressState});
export const changeAddressCity = (addressCity: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_CITY, {addressCity});
export const changeAddressZip = (addressZip: string) =>
  createAction(PolicyServiceRequestActionType.CHANGE_ADDRESS_ZIP, {addressZip});

const DEFAULT_STATE: PolicyServiceRequestState = {
  serviceRequest: EmptyPolicyServiceRequest,
};

type PolicyServiceRequestState = {
  serviceRequest: PolicyServiceRequest;
};

type PolicyServiceRequestAction =
  | ReturnType<typeof addAdditionalPolicyNumber>
  | ReturnType<typeof changeAdditionalPolicies>
  | ReturnType<typeof removeAdditionalPolicyNumber>
  | ReturnType<typeof submitInitialRequest>
  | ReturnType<typeof changeRequestType>
  | ReturnType<typeof clear>
  | ReturnType<typeof changePolicyNumber>
  | ReturnType<typeof changeInsuredFirstName>
  | ReturnType<typeof changeInsuredLastName>
  | ReturnType<typeof changeInsuredMiddleName>
  | ReturnType<typeof changePremiumMode>
  | ReturnType<typeof changeAccountNumber>
  | ReturnType<typeof changeControlNumber>
  | ReturnType<typeof changePayorName>
  | ReturnType<typeof changePlacePolicyOnDirectBill>
  | ReturnType<typeof changeAddressLastName>
  | ReturnType<typeof changeAddressFirstName>
  | ReturnType<typeof changeAddressMiddleName>
  | ReturnType<typeof changeAddressStreet>
  | ReturnType<typeof changeAddressCity>
  | ReturnType<typeof changeAddressState>
  | ReturnType<typeof changeAddressZip>;

export default (
  state: PolicyServiceRequestState = DEFAULT_STATE,
  action: PolicyServiceRequestAction
): PolicyServiceRequestState => {
  switch (action.type) {
    case PolicyServiceRequestActionType.CLEAR:
      return DEFAULT_STATE;
    case PolicyServiceRequestActionType.CHANGE_ADDITIONAL_POLICIES:
      const mergeAdditionalPolicyNumbers = (policyNumbers: PolicyNumber[]) =>
        policyNumbers.map(policyNum =>
          policyNum.id !== action.payload.additionalPolicyId
            ? policyNum
            : {id: action.payload.additionalPolicyId, value: NonEmptyString.create(action.payload.policyNumber)}
        );

      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            initial.insuredLastName,
            initial.insuredFirstName,
            initial.insuredMiddleName,
            mergeAdditionalPolicyNumbers(initial.additionalPolicyNumbers),
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.ADD_ADDITIONAL_POLICY_NUMBER:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            initial.insuredLastName,
            initial.insuredFirstName,
            initial.insuredMiddleName,
            [...initial.additionalPolicyNumbers, {id: nanoid(), value: new EmptyString()}],
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.CHANGE_POLICY_NUMBER:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            NonEmptyString.create(action.payload.policyNumber),
            initial.insuredLastName,
            initial.insuredFirstName,
            initial.insuredMiddleName,
            initial.additionalPolicyNumbers,
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.CHANGE_INSURED_FIRST_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            initial.insuredLastName,
            NonEmptyString.create(action.payload.insuredFirstName),
            initial.insuredMiddleName,
            initial.additionalPolicyNumbers,
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.CHANGE_INSURED_LAST_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            NonEmptyString.create(action.payload.insuredLastName),
            initial.insuredFirstName,
            initial.insuredMiddleName,
            initial.additionalPolicyNumbers,
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.CHANGE_INSURED_MIDDLE_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            initial.insuredLastName,
            initial.insuredFirstName,
            NonEmptyString.create(action.payload.insuredMiddleName),
            initial.additionalPolicyNumbers,
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.REMOVE_ADDITIONAL_POLICY_NUMBER:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            initial.insuredLastName,
            initial.insuredFirstName,
            initial.insuredMiddleName,
            initial.additionalPolicyNumbers.filter(
              policyNumber => policyNumber.id !== action.payload.additionalPolicyId
            ),
            initial.requestChangeType
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.CHANGE_SERVICE_REQUEST_TYPE:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: initial => ({
          ...state,
          serviceRequest: ValidInitialPolicyServiceRequest.create(
            initial.policyNumber,
            initial.insuredLastName,
            initial.insuredFirstName,
            initial.insuredMiddleName,
            initial.additionalPolicyNumbers,
            O.fromNullable(action.payload.changeType)
          ),
        }),
        WithChangeRequest: () => state,
      });
    case PolicyServiceRequestActionType.SUBMIT_INITIAL_REQUEST:
      const emptyChangeRequest =
        action.payload.validInitialRequest.requestChangeType.value === ChangeType.AddressChange
          ? EmptyChangeAddressRequest
          : action.payload.validInitialRequest.requestChangeType.value === ChangeType.BillingScheduleChange
          ? EmptyDirectBillingScheduleChange
          : EmptyChangePayrollAllotment;

      return {
        ...state,
        serviceRequest: new InvalidServiceRequestWithChange(action.payload.validInitialRequest, emptyChangeRequest),
      };
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_FIRST_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  NonEmptyString.create(action.payload.addressFirstName),
                  address.middleName,
                  address.lastName,
                  address.street,
                  address.city,
                  address.state,
                  address.zip
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_LAST_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  address.firstName,
                  address.middleName,
                  NonEmptyString.create(action.payload.addressLastName),
                  address.street,
                  address.city,
                  address.state,
                  address.zip
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_MIDDLE_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  address.firstName,
                  NonEmptyString.create(action.payload.addressMiddleName),
                  address.lastName,
                  address.street,
                  address.city,
                  address.state,
                  address.zip
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_STREET:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  address.firstName,
                  address.middleName,
                  address.lastName,
                  NonEmptyString.create(action.payload.addressStreet),
                  address.city,
                  address.state,
                  address.zip
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_CITY:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  address.firstName,
                  address.middleName,
                  address.lastName,
                  address.street,
                  NonEmptyString.create(action.payload.addressCity),
                  address.state,
                  address.zip
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_STATE:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  address.firstName,
                  address.middleName,
                  address.lastName,
                  address.street,
                  address.city,
                  NonEmptyString.create(action.payload.addressState),
                  address.zip
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ADDRESS_ZIP:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            AddressChangeRequest: address =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeAddress.create(
                  address.firstName,
                  address.middleName,
                  address.lastName,
                  address.street,
                  address.city,
                  address.state,
                  NonEmptyString.create(action.payload.addressZip)
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_PREMIUM_MODE:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            DirectBillingChangeRequest: () =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangeDirectBillingSchedule.create(O.fromNullable(action.payload.premiumMode))
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_ACCOUNT_NUMBER:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            PayrollAllotmentChangeRequest: payrollChange =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangePayrollAllotment.create(
                  NonEmptyString.create(action.payload.accountNumber),
                  payrollChange.controlNumber,
                  payrollChange.payorName,
                  payrollChange.billingSchedule
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_CONTROL_NUMBER:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            PayrollAllotmentChangeRequest: payrollChange =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangePayrollAllotment.create(
                  payrollChange.accountNumber,
                  NonEmptyString.create(action.payload.controlNumber),
                  payrollChange.payorName,
                  payrollChange.billingSchedule
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_PAYOR_NAME:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            PayrollAllotmentChangeRequest: payrollChange =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangePayrollAllotment.create(
                  payrollChange.accountNumber,
                  payrollChange.controlNumber,
                  NonEmptyString.create(action.payload.payorName),
                  payrollChange.billingSchedule
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    case PolicyServiceRequestActionType.CHANGE_PLACE_POLICY_ON_DIRECT_BILL:
      return matchPolicyServiceRequest(state.serviceRequest, {
        Initial: () => state,
        WithChangeRequest: withChangeRequest => ({
          ...state,
          serviceRequest: matchChangeRequest(withChangeRequest.changeRequest, {
            PayrollAllotmentChangeRequest: payrollChange =>
              ValidServiceRequestWithChange.create(
                withChangeRequest.initialPolicyServiceRequestInformation,
                ValidChangePayrollAllotment.create(
                  payrollChange.accountNumber,
                  payrollChange.controlNumber,
                  payrollChange.payorName,
                  O.fromNullable(action.payload.placePolicyOnBill)
                )
              ),
            default: () => withChangeRequest,
          }),
        }),
      });
    default:
      return state;
  }
};
